import MarkdownIt from 'markdown-it';
import { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { encodePath } from '../shared/utils/format';

/**
 * Pages are markdown with an optional frontmatter block, and links between them are `[[wikilinks]]`.
 * Everything here treats the text as hostile:
 *
 * - raw HTML is switched off, so the only markup that can reach the page is what this renderer emits;
 * - a link may only be http(s), mailto, a `#fragment`, or a path inside the same repository; every other
 *   scheme (`javascript:`, `data:`, `file:`, app-specific schemes) is refused and left as literal text;
 * - a relative link is re-built from the repository's own file list, never echoed as written;
 * - images are never loaded: a page cannot make the reader's browser contact another site;
 * - bare file names such as `notes.md` are not turned into links to a `.md` web domain.
 */

export interface Frontmatter {
  entries: [string, string | string[]][];
  body: string;
}

export function splitFrontmatter(text: string): Frontmatter {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(text);
  if (!match) return { entries: [], body: text };
  const entries: [string, string | string[]][] = [];
  let currentKey: string | null = null;
  let list: string[] | null = null;
  for (const line of match[1]!.split(/\r?\n/)) {
    const item = /^\s+-\s+(.*)$/.exec(line);
    if (item && currentKey && list) {
      list.push(unquote(item[1]!));
      continue;
    }
    const pair = /^([A-Za-z0-9_-]+):\s*(.*)$/.exec(line);
    if (!pair) continue;
    if (list && currentKey) entries.push([currentKey, list]);
    currentKey = pair[1]!;
    const value = pair[2]!.trim();
    if (value === '') {
      list = [];
    } else {
      list = null;
      const flow = /^\[(.*)\]$/.exec(value);
      entries.push([
        currentKey,
        flow
          ? flow[1]!
              .split(',')
              .map((s) => unquote(s.trim()))
              .filter(Boolean)
          : unquote(value),
      ]);
      currentKey = null;
    }
  }
  if (list && currentKey) entries.push([currentKey, list]);
  return { entries, body: text.slice(match[0].length) };
}

const unquote = (s: string): string => s.replace(/^(['"])(.*)\1$/, '$2');

export interface PageIndex {
  /** Resolve a wikilink target to the path of a file in the repository, if there is one. */
  resolve: (target: string) => string | null;
  /** Whether a file with exactly this repository path exists in the revision. */
  has: (path: string) => boolean;
}

export function buildPageIndex(paths: string[]): PageIndex {
  const all = new Set(paths);
  const bySlug = new Map<string, string>();
  const byPath = new Map<string, string>();
  for (const path of [...paths].sort()) {
    if (!/\.md$/i.test(path)) continue;
    const noExt = path.replace(/\.md$/i, '');
    const slug = noExt.split('/').pop()!.toLowerCase();
    if (!bySlug.has(slug)) bySlug.set(slug, path);
    byPath.set(noExt.toLowerCase(), path);
    if (noExt.startsWith('wiki/')) byPath.set(noExt.slice(5).toLowerCase(), path);
  }
  return {
    has: (path) => all.has(path),
    resolve(target) {
      const clean = target.trim().replace(/\.md$/i, '').toLowerCase();
      if (!clean) return null;
      return (
        byPath.get(clean) ??
        bySlug.get(clean) ??
        bySlug.get(clean.replace(/\s+/g, '-')) ??
        bySlug.get(clean.replace(/[\s_]+/g, '-')) ??
        null
      );
    },
  };
}

export interface RenderContext {
  index: PageIndex | null;
  /** `/owner/repo`, the prefix of every address under the repository. */
  base: string;
  rev: string | null;
  /** The repository path of the file being shown, so relative links resolve from its folder. */
  filePath?: string;
}

const EXTERNAL = /^https?:/i;
const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** Where a relative or root-relative link points inside the repository, or `null` if it escapes it. */
export function resolveRepoPath(href: string, fromFile: string | undefined): string | null {
  const clean = href.replace(/[?#].*$/, '');
  if (!clean) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(clean);
  } catch {
    return null;
  }
  const out = decoded.startsWith('/') ? [] : (fromFile ?? '').split('/').slice(0, -1);
  for (const segment of decoded.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      if (out.length === 0) return null;
      out.pop();
      continue;
    }
    out.push(segment);
  }
  return out.length > 0 ? out.join('/') : null;
}

const pageHref = (ctx: RenderContext, path: string) =>
  `${ctx.base}/blob/${encodePath(path)}${ctx.rev ? `?rev=${encodeURIComponent(ctx.rev)}` : ''}`;

const headingSlug = (label: string) =>
  label
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s_-]/gu, '')
    .replace(/\s+/g, '-') || 'section';
const withFragment = (href: string, source: string) => {
  const fragment = source.includes('#') ? source.slice(source.indexOf('#') + 1) : '';
  if (!fragment) return href;
  try {
    const decoded = decodeURIComponent(fragment);
    return `${href}#${encodeURIComponent(decoded.startsWith('heading-') ? decoded : `heading-${headingSlug(decoded)}`)}`;
  } catch {
    return href;
  }
};

function createRenderer(ctx: RenderContext) {
  const { index } = ctx;
  const md = new MarkdownIt({
    html: false,
    linkify: true,
    typographer: false,
    breaks: false,
  });
  // "notes.md" and "build.sh" are file names, not web addresses: only explicit http(s):// and mailto: link.
  md.linkify.set({ fuzzyLink: false, fuzzyEmail: false, fuzzyIP: false });
  const fence = md.renderer.rules.fence!;
  md.renderer.rules.fence = (...args) => fence(...args).replace('<pre>', '<pre tabindex="0">');
  md.renderer.rules.table_open = (tokens, at, options, _env, self) => {
    tokens[at]!.attrSet('tabindex', '0');
    return self.renderToken(tokens, at, options);
  };
  md.validateLink = (url) => {
    const href = url.trim();
    if (SCHEME.test(href)) return EXTERNAL.test(href) || /^mailto:/i.test(href);
    return !href.startsWith('//');
  };

  md.inline.ruler.before('link', 'wikilink', (state, silent) => {
    const src = state.src;
    if (src.charCodeAt(state.pos) !== 0x5b || src.charCodeAt(state.pos + 1) !== 0x5b) return false;
    const end = src.indexOf(']]', state.pos + 2);
    if (end < 0) return false;
    const inner = src.slice(state.pos + 2, end);
    if (!inner || inner.includes('\n') || inner.includes('[')) return false;
    if (!silent) {
      const [targetPart, alias] = inner.split('|');
      const target = (targetPart ?? '').split('#')[0]!.trim();
      const label = (alias ?? targetPart ?? '').trim();
      if (!index) {
        // The repository's file list has not arrived yet, so nothing can be said about this link.
        state.push('text', '', 0).content = label;
        state.pos = end + 2;
        return true;
      }
      const resolved = index.resolve(target);
      const open = state.push('wikilink_open', 'a', 1);
      open.attrs = resolved
        ? [
            ['href', withFragment(pageHref(ctx, resolved), targetPart ?? '')],
            ['class', 'wikilink'],
            ['data-internal', 'true'],
          ]
        : [
            ['class', 'wikilink unresolved'],
            ['title', `No page named “${target}” in this wiki`],
          ];
      state.push('text', '', 0).content = label;
      state.push('wikilink_close', 'a', -1);
    }
    state.pos = end + 2;
    return true;
  });
  // An unresolved link has no href, so it renders as an anchor without a destination.
  md.renderer.rules.wikilink_open = (tokens, i) => {
    const t = tokens[i]!;
    const attrs = (t.attrs ?? []).map(([k, v]) => ` ${k}="${md.utils.escapeHtml(v)}"`).join('');
    return `<a${attrs}>`;
  };
  md.renderer.rules.wikilink_close = () => '</a>';

  const defaultLink =
    md.renderer.rules.link_open ?? ((tokens, i, options, _env, self) => self.renderToken(tokens, i, options));
  md.renderer.rules.link_open = (tokens, i, options, env, self) => {
    const token = tokens[i]!;
    const href = token.attrGet('href') ?? '';
    if (EXTERNAL.test(href)) {
      token.attrSet('rel', 'noopener noreferrer nofollow');
      token.attrSet('target', '_blank');
    } else if (href.startsWith('#')) {
      token.attrSet('href', withFragment('', href));
    } else if (!/^mailto:/i.test(href)) {
      // A path inside the repository. It is rebuilt from the file list or dropped; it is never echoed.
      const path = resolveRepoPath(href, ctx.filePath);
      const target =
        path && index ? (index.has(path) ? path : index.has(`${path}.md`) ? `${path}.md` : null) : null;
      token.attrs = (token.attrs ?? []).filter(([key]) => key !== 'href');
      if (target) {
        token.attrSet('href', withFragment(pageHref(ctx, target), href));
        token.attrSet('class', 'wikilink');
        token.attrSet('data-internal', 'true');
      } else {
        token.attrSet('class', 'wikilink unresolved');
        token.attrSet('title', index ? 'No such file in this wiki' : 'Checking this link…');
      }
    }
    return defaultLink(tokens, i, options, env, self);
  };

  // A page cannot make the reader's browser fetch anything: an image is shown as its description, with a
  // link to the address only if the reader chooses to follow it.
  md.renderer.rules.image = (tokens, i, options, env, self) => {
    const token = tokens[i]!;
    const alt = self.renderInlineAsText(token.children ?? [], options, env) || 'image';
    const src = token.attrGet('src') ?? '';
    const label = `[image: ${md.utils.escapeHtml(alt)}]`;
    return EXTERNAL.test(src)
      ? `<a class="remote-image" href="${md.utils.escapeHtml(src)}" rel="noopener noreferrer nofollow" target="_blank">${label}</a>`
      : `<span class="remote-image">${label}</span>`;
  };
  return md;
}

/** Markdown text to HTML. Exported so the safety rules above can be tested without a browser. */
export function renderMarkdown(body: string, ctx: RenderContext): string {
  return renderDocument(body, ctx).html;
}

/** Heading IDs and the contents list are derived from the same parsed Markdown tokens. */
export function renderDocument(body: string, ctx: RenderContext) {
  const md = createRenderer(ctx);
  const tokens = md.parse(body, {});
  const headings: { id: string; label: string; level: number }[] = [];
  const seen = new Map<string, number>();
  tokens.forEach((token, at) => {
    if (token.type !== 'heading_open') return;
    const inline = tokens[at + 1];
    const label = (inline?.children ?? [])
      .filter((child) => ['text', 'code_inline', 'image'].includes(child.type))
      .map((child) => child.content)
      .join('');
    const slug = headingSlug(label);
    const count = seen.get(slug) ?? 0;
    seen.set(slug, count + 1);
    const id = `heading-${slug}${count ? `-${count}` : ''}`;
    token.attrSet('id', id);
    headings.push({ id, label, level: Number(token.tag.slice(1)) });
  });
  return { html: md.renderer.render(tokens, md.options, {}), headings };
}

export function Markdown({
  text,
  base,
  rev,
  index,
  filePath,
  showFrontmatter = true,
  showContents = true,
}: {
  text: string;
  /** `/owner/repo`, used to resolve wikilinks to pages. */
  base: string;
  rev: string | null;
  index: PageIndex | null;
  /** Path of the file being rendered, so a relative link such as `../notes.md` resolves from its folder. */
  filePath?: string;
  showFrontmatter?: boolean;
  /** Conversation text is short and has no use for an "On this page" panel. */
  showContents?: boolean;
}) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { entries, body } = useMemo(() => splitFrontmatter(text), [text]);
  const { html, headings } = useMemo(
    () => renderDocument(body, { index, base, rev, filePath }),
    [body, index, base, rev, filePath],
  );
  useEffect(() => {
    let id: string;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return;
    }
    if (headings.some((heading) => heading.id === id)) document.getElementById(id)?.scrollIntoView();
  }, [hash, html]);

  return (
    <div>
      {showFrontmatter && entries.length > 0 && (
        <FrontmatterPanel entries={entries} base={base} rev={rev} index={index} />
      )}
      {showContents && headings.length >= 4 && (
        <details className="page-contents" open>
          <summary>On this page</summary>
          <nav aria-label="Page contents">
            <ul>
              {headings.map((heading) => (
                <li key={heading.id} className={`toc-level-${heading.level}`}>
                  <a href={`#${heading.id}`}>{heading.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        </details>
      )}
      <div
        className="markdown wiki-prose"
        // The renderer is configured with html:false, so this string is markup this module produced.
        dangerouslySetInnerHTML={{ __html: html }}
        onClick={(event) => {
          // Internal links navigate inside the app instead of reloading it.
          const anchor = (event.target as HTMLElement).closest('a[data-internal]');
          if (anchor && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
            event.preventDefault();
            navigate(anchor.getAttribute('href')!);
          }
        }}
      />
    </div>
  );
}

function FrontmatterPanel({
  entries,
  base,
  rev,
  index,
}: {
  entries: [string, string | string[]][];
  base: string;
  rev: string | null;
  index: PageIndex | null;
}) {
  const link = (value: string) => {
    const target = value.replace(/^\[\[|\]\]$/g, '');
    const resolved = index?.resolve(target);
    return resolved ? (
      <Link
        key={value}
        to={`${base}/blob/${encodePath(resolved)}${rev ? `?rev=${encodeURIComponent(rev)}` : ''}`}
      >
        {target}
      </Link>
    ) : (
      <span key={value}>{target}</span>
    );
  };
  return (
    <dl className="frontmatter" aria-label="Page properties">
      {entries.map(([key, value], at) => (
        <div key={`${key}-${at}`} style={{ display: 'contents' }}>
          <dt>{key}</dt>
          <dd>
            {Array.isArray(value)
              ? value.map((v) =>
                  key === 'related' || key === 'relations' || /^\[\[/.test(v) ? (
                    link(v.replace(/^\w+\s+(?=\[\[)/, ''))
                  ) : (
                    <span className="tag" key={v}>
                      {v}
                    </span>
                  ),
                )
              : value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
